import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Typography, Paper } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { ArrowDownward as ArrowDownIcon, ArrowBack as ArrowBackIcon, GetApp as ExportIcon } from '@material-ui/icons'
import { Toast } from 'components/shared/Toaster'
import strings from 'strings'
import { deriveComparisonVerdict } from './bankDocsAdapters'
import exportComparisonPdf from './exportComparisonPdf'

const BANNER_BG = '#152420'

const useStyles = makeStyles((theme) => ({
  topBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2)
  },
  backLink: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    color: '#555'
  },
  generatedLabel: {
    fontSize: 13,
    color: 'var(--neutral-400)'
  },
  exportButton: {
    backgroundColor: BANNER_BG,
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '8px 16px',
    fontWeight: 600,
    fontSize: 13,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 6
  },
  banner: {
    backgroundColor: BANNER_BG,
    color: '#fff',
    borderRadius: '14px',
    padding: theme.spacing(3),
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(3),
    marginBottom: theme.spacing(3)
  },
  bannerEyebrow: {
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.08em',
    color: 'var(--status-pending)',
    marginBottom: theme.spacing(1)
  },
  bannerHeadline: {
    fontSize: '26px',
    fontWeight: 700,
    marginBottom: theme.spacing(1.5)
  },
  bannerDescription: {
    fontSize: '14px',
    color: 'rgba(255,255,255,0.75)',
    maxWidth: '640px',
    lineHeight: 1.6
  },
  scorePairBox: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderRadius: '10px',
    padding: theme.spacing(1.5, 2),
    flexShrink: 0,
    height: 'fit-content'
  },
  scoreBoxLabel: {
    fontSize: '10px',
    fontWeight: 700,
    color: 'rgba(255,255,255,0.5)',
    letterSpacing: '0.05em'
  },
  scoreBoxValue: {
    fontSize: '22px',
    fontWeight: 700
  },
  deltaCardsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3)
  },
  deltaCardCol: {
    flex: '1 1 220px',
    minWidth: '200px'
  },
  bucketsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginTop: theme.spacing(1)
  },
  bucketCol: {
    flex: '1 1 260px',
    minWidth: '240px'
  },
  deltaCard: {
    padding: theme.spacing(2),
    borderRadius: '12px',
    border: '1px solid #E0E0E0',
    boxShadow: 'none',
    height: '100%'
  },
  deltaLabel: {
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.04em',
    color: 'var(--neutral-400)'
  },
  deltaValue: {
    fontSize: '24px',
    fontWeight: 700,
    marginTop: theme.spacing(0.5)
  },
  deltaSubtext: {
    fontSize: '12px',
    color: 'var(--neutral-400)',
    marginTop: theme.spacing(0.5)
  },
  deltaBarTrack: {
    height: '4px',
    borderRadius: '2px',
    backgroundColor: '#EFEFEF',
    marginTop: theme.spacing(1),
    overflow: 'hidden'
  },
  deltaBarFill: {
    height: '100%',
    borderRadius: '2px'
  },
  sectionCard: {
    padding: theme.spacing(3),
    borderRadius: '12px',
    border: '1px solid #E0E0E0',
    boxShadow: 'none'
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: 700
  },
  sectionSubtitle: {
    fontSize: '13px',
    color: 'var(--neutral-400)',
    marginTop: theme.spacing(0.25)
  },
  popLegend: {
    display: 'flex',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginTop: theme.spacing(1)
  },
  popLegendItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 12,
    color: '#555'
  },
  popLegendSwatch: {
    width: 10,
    height: 10,
    borderRadius: 3,
    display: 'inline-block'
  },
  popRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(3),
    marginTop: theme.spacing(2)
  },
  popGroup: {
    flex: '1 1 200px',
    minWidth: '180px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  popPlot: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    gap: theme.spacing(2),
    width: '100%',
    height: '240px',
    paddingTop: '24px',
    boxSizing: 'border-box'
  },
  popBarSlot: {
    position: 'relative',
    flex: '0 1 56px',
    maxWidth: '56px',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end'
  },
  popBar: {
    width: '100%',
    borderRadius: '4px 4px 0 0',
    minHeight: '2px',
    transition: 'height 0.2s ease'
  },
  popValue: {
    fontSize: '12px',
    fontWeight: 700,
    color: '#2d2d2d',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    marginBottom: 4
  },
  popMetricLabel: {
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.04em',
    color: 'var(--neutral-400)',
    marginTop: theme.spacing(1.5),
    textAlign: 'center'
  },
  changeBucket: {
    borderRadius: '10px',
    padding: theme.spacing(2),
    height: '100%'
  },
  bucketTitle: {
    fontSize: '12px',
    fontWeight: 700,
    letterSpacing: '0.04em',
    marginBottom: theme.spacing(1.5)
  },
  bucketChip: {
    display: 'block',
    marginBottom: theme.spacing(1),
    fontSize: 12,
    fontWeight: 600,
    padding: theme.spacing(1),
    borderRadius: '8px'
  },
  resolvedNote: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: 'var(--bg-canvas)',
    borderRadius: '10px',
    fontSize: 13,
    color: '#555',
    lineHeight: 1.6
  },
  tabsRow: {
    display: 'flex',
    gap: theme.spacing(3),
    borderBottom: '1px solid #EFEFEF',
    marginBottom: theme.spacing(2)
  },
  tabItem: {
    padding: theme.spacing(1, 0),
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--neutral-400)',
    cursor: 'pointer',
    borderBottom: '2px solid transparent'
  },
  tabItemActive: {
    color: '#152420',
    borderBottomColor: '#9b8afb'
  },
  analysisRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    fontSize: 14,
    lineHeight: 1.6
  },
  analysisText: {
    flex: 1
  },
  analysisIndex: {
    fontSize: 12,
    fontWeight: 700,
    color: '#bdbdbd',
    minWidth: '20px'
  }
}))

const ANALYSIS_TABS = [
  { key: 'accountStateAnalysis', labelKey: 'BANK_STATEMENT_ACCOUNT_STATE_TAB' },
  { key: 'overallHealthAnalysis', labelKey: 'BANK_STATEMENT_OVERALL_HEALTH_TAB' },
  { key: 'takeAwayAnalysis', labelKey: 'BANK_STATEMENT_TAKEAWAY_ANALYSIS_TAB' }
]

const formatPercent = (value) => `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`

const StatementComparison = ({ fromDoc, toDoc, fromLabel, toLabel, comparisonData, onBack, formatCurrency }) => {
  const classes = useStyles()
  const [activeTab, setActiveTab] = useState(ANALYSIS_TABS[0].key)

  if (!comparisonData) {
    return (
      <Paper className={classes.sectionCard}>
        <div className={classes.topBar}>
          <div className={classes.backLink} onClick={onBack}>
            <ArrowBackIcon fontSize="small" /> {strings.BANK_STATEMENT_BACK_TO_STATEMENT}
          </div>
        </div>
        <Typography>{strings.BANK_STATEMENT_NO_COMPARISON_DATA}</Typography>
      </Paper>
    )
  }

  const { report } = comparisonData
  const fromReport = fromDoc
  const toReport = toDoc

  const fromScore = fromReport.bankNotesInsightsData.healthScore
  const toScore = toReport.bankNotesInsightsData.healthScore
  const scoreImproved = toScore >= fromScore
  const verdict = deriveComparisonVerdict(report, fromScore, toScore)

  const fromEnding = fromReport.endBalance
  const toEnding = toReport.endBalance
  const endingBalanceChange = {
    absolute_delta: toEnding - fromEnding,
    percentage_change: fromEnding !== 0 ? ((toEnding - fromEnding) / fromEnding) * 100 : 0
  }

  const fromNet = fromReport.credits - fromReport.debits
  const toNet = toReport.credits - toReport.debits
  const netReversed = (fromNet >= 0) !== (toNet >= 0)

  const indicatorAnalysis = (report.healthIndicatorsAnalysis && report.healthIndicatorsAnalysis[0])
    || { new_indicators: [], persistent_indicators: [], resolved_indicators: [] }

  const handleExport = () => {
    try {
      exportComparisonPdf({
        fromLabel,
        toLabel,
        fromDoc: fromReport,
        toDoc: toReport,
        report,
        verdict,
        fromScore,
        toScore,
        indicatorAnalysis,
        formatCurrency
      })
      Toast({ type: 'success', message: strings.BANK_STATEMENT_EXPORT_SUCCESS })
    } catch (err) {
      Toast({ type: 'error', message: strings.BANK_STATEMENT_EXPORT_FAILED })
    }
  }

  // Built as positioned divs rather than an ApexCharts grouped bar. Two
  // reasons the Apex version was unreadable: (1) a single shared y-axis meant
  // the ~$18k ending-balance bars rendered a few pixels tall next to ~$381k
  // credits bars, so their labels collided; (2) Apex draws dataLabels inside
  // the bar with one fixed colour, which vanished on the dark bar. Scaling
  // each metric to its own max - what the section subtitle always claimed -
  // and placing values above the bars fixes both.
  const periodMetrics = [
    { label: strings.BANK_STATEMENT_CREDITS, from: fromReport.credits, to: toReport.credits },
    { label: strings.BANK_STATEMENT_DEBITS, from: fromReport.debits, to: toReport.debits },
    { label: strings.BANK_STATEMENT_ENDING_BALANCE, from: fromReport.endBalance, to: toReport.endBalance }
  ].map((metric) => {
    const scale = Math.max(Math.abs(metric.from), Math.abs(metric.to), 1)
    return {
      ...metric,
      fromPct: (Math.abs(metric.from) / scale) * 100,
      toPct: (Math.abs(metric.to) / scale) * 100
    }
  })

  const fromSeriesLabel = `${strings.BANK_STATEMENT_PREVIOUS} · ${fromLabel}`
  const toSeriesLabel = `${strings.BANK_STATEMENT_CURRENT} · ${toLabel}`

  return (
    <div>
      <div className={classes.topBar}>
        <div className={classes.backLink} onClick={onBack}>
          <ArrowBackIcon fontSize="small" /> {strings.BANK_STATEMENT_BACK_TO_STATEMENT}
        </div>
        <Typography className={classes.generatedLabel}>
          {strings.BANK_STATEMENT_COMPARISON_GENERATED_PREFIX} {new Date(report.comparisonDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
        </Typography>
        <button type="button" className={classes.exportButton} onClick={handleExport}>
          <ExportIcon style={{ fontSize: 16 }} /> {strings.BANK_STATEMENT_EXPORT_COMPARISON}
        </button>
      </div>

      <div className={classes.banner}>
        <div>
          <Typography className={classes.bannerEyebrow}>{fromLabel.toUpperCase()} → {toLabel.toUpperCase()}</Typography>
          <Typography className={classes.bannerHeadline}>{verdict.headline}</Typography>
          <Typography className={classes.bannerDescription}>{verdict.description}</Typography>
        </div>
        <div className={classes.scorePairBox}>
          <div>
            <Typography className={classes.scoreBoxLabel}>{fromLabel.split(' ')[0].slice(0, 3).toUpperCase()}</Typography>
            <Typography className={classes.scoreBoxValue} style={{ color: '#9b8afb' }}>{fromScore.toFixed(1)}</Typography>
          </div>
          <ArrowDownIcon style={{ color: scoreImproved ? '#4caf82' : 'var(--status-pending)', transform: scoreImproved ? 'rotate(180deg)' : 'none' }} />
          <div>
            <Typography className={classes.scoreBoxLabel}>{toLabel.split(' ')[0].slice(0, 3).toUpperCase()}</Typography>
            <Typography className={classes.scoreBoxValue} style={{ color: 'var(--status-pending)' }}>{toScore.toFixed(1)}</Typography>
          </div>
        </div>
      </div>

      <div className={classes.deltaCardsRow}>
        <div className={classes.deltaCardCol}>
          <Paper className={classes.deltaCard}>
            <Typography className={classes.deltaLabel}>{strings.BANK_STATEMENT_CREDITS}</Typography>
            <Typography className={classes.deltaValue} style={{ color: report.creditChange.percentage_change >= 0 ? 'var(--status-approved)' : 'var(--status-pending)' }}>
              {formatPercent(report.creditChange.percentage_change)}
            </Typography>
            <Typography className={classes.deltaSubtext}>
              {formatCurrency(fromReport.credits)} → {formatCurrency(toReport.credits)}
            </Typography>
            <div className={classes.deltaBarTrack}>
              <div className={classes.deltaBarFill} style={{ width: `${Math.min(100, Math.abs(report.creditChange.percentage_change))}%`, backgroundColor: 'var(--status-pending)' }} />
            </div>
          </Paper>
        </div>
        <div className={classes.deltaCardCol}>
          <Paper className={classes.deltaCard}>
            <Typography className={classes.deltaLabel}>{strings.BANK_STATEMENT_DEBITS}</Typography>
            <Typography className={classes.deltaValue} style={{ color: '#9b8afb' }}>
              {formatPercent(report.debitChange.percentage_change)}
            </Typography>
            <Typography className={classes.deltaSubtext}>
              {formatCurrency(fromReport.debits)} → {formatCurrency(toReport.debits)}
            </Typography>
            <div className={classes.deltaBarTrack}>
              <div className={classes.deltaBarFill} style={{ width: `${Math.min(100, Math.abs(report.debitChange.percentage_change))}%`, backgroundColor: '#9b8afb' }} />
            </div>
          </Paper>
        </div>
        <div className={classes.deltaCardCol}>
          <Paper className={classes.deltaCard}>
            <Typography className={classes.deltaLabel}>{strings.BANK_STATEMENT_NET_CASH_FLOW}</Typography>
            <Typography className={classes.deltaValue} style={{ color: 'var(--status-pending)' }}>
              {netReversed ? 'Reversed' : formatPercent(((toNet - fromNet) / (Math.abs(fromNet) || 1)) * 100)}
            </Typography>
            <Typography className={classes.deltaSubtext}>
              {fromNet >= 0 ? '+' : ''}{formatCurrency(fromNet)} → {toNet >= 0 ? '+' : ''}{formatCurrency(toNet)}
            </Typography>
            <div className={classes.deltaBarTrack}>
              <div className={classes.deltaBarFill} style={{ width: '100%', backgroundColor: 'var(--status-pending)' }} />
            </div>
          </Paper>
        </div>
        <div className={classes.deltaCardCol}>
          <Paper className={classes.deltaCard}>
            <Typography className={classes.deltaLabel}>{strings.BANK_STATEMENT_ENDING_BALANCE}</Typography>
            <Typography className={classes.deltaValue} style={{ color: endingBalanceChange.percentage_change >= 0 ? 'var(--status-approved)' : 'var(--status-pending)' }}>
              {formatPercent(endingBalanceChange.percentage_change)}
            </Typography>
            <Typography className={classes.deltaSubtext}>
              {formatCurrency(fromEnding)} → {formatCurrency(toEnding)}
            </Typography>
            <div className={classes.deltaBarTrack}>
              <div className={classes.deltaBarFill} style={{ width: `${Math.min(100, Math.abs(endingBalanceChange.percentage_change))}%`, backgroundColor: 'var(--status-pending)' }} />
            </div>
          </Paper>
        </div>
      </div>

      <Paper className={classes.sectionCard} style={{ marginBottom: 24 }}>
        <Typography className={classes.sectionTitle}>{strings.BANK_STATEMENT_PERIOD_OVER_PERIOD}</Typography>
        <Typography className={classes.sectionSubtitle}>
          {strings.BANK_STATEMENT_PERIOD_OVER_PERIOD_SUBTITLE}
        </Typography>

        <div className={classes.popLegend}>
          <span className={classes.popLegendItem}>
            <span className={classes.popLegendSwatch} style={{ backgroundColor: BANNER_BG }} />
            {fromSeriesLabel}
          </span>
          <span className={classes.popLegendItem}>
            <span className={classes.popLegendSwatch} style={{ backgroundColor: '#9b8afb' }} />
            {toSeriesLabel}
          </span>
        </div>

        <div className={classes.popRow}>
          {periodMetrics.map((metric) => (
            <div className={classes.popGroup} key={metric.label}>
              <div className={classes.popPlot}>
                <div className={classes.popBarSlot}>
                  <Typography className={classes.popValue}>{formatCurrency(metric.from)}</Typography>
                  <div
                    className={classes.popBar}
                    style={{ height: `${metric.fromPct}%`, backgroundColor: BANNER_BG }}
                  />
                </div>
                <div className={classes.popBarSlot}>
                  <Typography className={classes.popValue}>{formatCurrency(metric.to)}</Typography>
                  <div
                    className={classes.popBar}
                    style={{ height: `${metric.toPct}%`, backgroundColor: '#9b8afb' }}
                  />
                </div>
              </div>
              <Typography className={classes.popMetricLabel}>{metric.label}</Typography>
            </div>
          ))}
        </div>
      </Paper>

      <Paper className={classes.sectionCard} style={{ marginBottom: 24 }}>
        <Typography className={classes.sectionTitle}>{strings.BANK_STATEMENT_WHAT_CHANGED}</Typography>
        <Typography className={classes.sectionSubtitle}>
          {indicatorAnalysis.resolved_indicators.length} {strings.BANK_STATEMENT_RESOLVED.toLowerCase()},{' '}
          {indicatorAnalysis.new_indicators.length} {strings.BANK_STATEMENT_NEW.toLowerCase()},{' '}
          {indicatorAnalysis.persistent_indicators.length} {strings.BANK_STATEMENT_PERSISTENT.toLowerCase()}
        </Typography>
        <div className={classes.bucketsRow}>
          <div className={classes.bucketCol}>
            <div className={classes.changeBucket} style={{ backgroundColor: '#FCEFE9' }}>
              <Typography className={classes.bucketTitle} style={{ color: 'var(--status-pending)' }}>{strings.BANK_STATEMENT_NEW} · {indicatorAnalysis.new_indicators.length}</Typography>
              {indicatorAnalysis.new_indicators.map((text, i) => (
                <Typography key={i} className={classes.bucketChip} style={{ backgroundColor: '#fff' }}>{text}</Typography>
              ))}
            </div>
          </div>
          <div className={classes.bucketCol}>
            <div className={classes.changeBucket} style={{ backgroundColor: '#EDEBFB' }}>
              <Typography className={classes.bucketTitle} style={{ color: '#9b8afb' }}>{strings.BANK_STATEMENT_PERSISTENT} · {indicatorAnalysis.persistent_indicators.length}</Typography>
              {indicatorAnalysis.persistent_indicators.map((text, i) => (
                <Typography key={i} className={classes.bucketChip} style={{ backgroundColor: '#fff' }}>{text}</Typography>
              ))}
            </div>
          </div>
          <div className={classes.bucketCol}>
            <div className={classes.changeBucket} style={{ backgroundColor: '#EDF6F0' }}>
              <Typography className={classes.bucketTitle} style={{ color: '#4caf82' }}>{strings.BANK_STATEMENT_RESOLVED} · {indicatorAnalysis.resolved_indicators.length}</Typography>
              {indicatorAnalysis.resolved_indicators.map((text, i) => (
                <Typography key={i} className={classes.bucketChip} style={{ backgroundColor: '#fff', textDecoration: 'line-through', color: 'var(--neutral-400)' }}>{text}</Typography>
              ))}
            </div>
          </div>
        </div>
        <div className={classes.resolvedNote}>{strings.BANK_STATEMENT_RESOLVED_EXPLAINER}</div>
      </Paper>

      <Paper className={classes.sectionCard}>
        <div className={classes.tabsRow}>
          {ANALYSIS_TABS.map((tab) => (
            <div
              key={tab.key}
              className={`${classes.tabItem} ${activeTab === tab.key ? classes.tabItemActive : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {strings[tab.labelKey]}
            </div>
          ))}
        </div>
        {(report[activeTab] || []).map((line, index) => (
          <div className={classes.analysisRow} key={index}>
            <Typography className={classes.analysisIndex}>{String(index + 1).padStart(2, '0')}</Typography>
            <Typography className={classes.analysisText}>{line}</Typography>
          </div>
        ))}
      </Paper>
    </div>
  )
}

StatementComparison.propTypes = {
  fromDoc: PropTypes.object.isRequired,
  toDoc: PropTypes.object.isRequired,
  fromLabel: PropTypes.string.isRequired,
  toLabel: PropTypes.string.isRequired,
  comparisonData: PropTypes.object,
  onBack: PropTypes.func.isRequired,
  formatCurrency: PropTypes.func.isRequired
}

export default StatementComparison
